import { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import * as yup from 'yup';
import { Button, Grid } from '@material-ui/core';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { clearClient, clearUpsertClientResult, upsertClientAsync, getClientAsync, saveClient, setMessage } from '../../../../store/adviser';
import FormikWrapper from '../../../containers/FormikWrapper';
import { Client, ScreenProps, SendApplicationToClientResult } from '../../../../common/types';
import { TitleText } from '../../../controls/helpers';
import SelectProduct from '../../../application/SelectProduct';
import SendApplicationToClient from '../../../application/SendApplicationToClient';
import { Product } from '../../../../api';
import { getRoles, hasSelectedProductRoles } from '../../Helpers';

enum FormMode {
    None,
    Confirm,
    Submit,
    Send,
    Get
}

const SelectProducts = ({ history, match, nextRoute }: RouteComponentProps & ScreenProps) => {
    const { client, upsertClientResult } = useAppSelector(state => state.adviser);
    const [formMode, setFormMode] = useState<FormMode>(FormMode.None);
    const clientId = (match.params as { clientId: string }).clientId;
    const dispatch = useAppDispatch();
    const { user } = useAppSelector(state => state.oidc);

    const cleanValues = (values: Client) => ({
        ...values,
        kiwiSaverApplication: { ...values.kiwiSaverApplication, active: values.products?.includes(Product.KiwiSaver) },
        wealthBuilderApplications: values.wealthBuilderApplications?.map(a => ({ ...a, active: values.products?.includes(Product.WealthBuilder) })),
        incomeGeneratorApplications: values.incomeGeneratorApplications?.map(a => ({ ...a, active: values.products?.includes(Product.IncomeGenerator) }))
    });

    const openSendToClient = (values: Client) => {
        dispatch(saveClient(cleanValues(values)));
        setFormMode(FormMode.Confirm);
    }

    const sendToClient = (result: SendApplicationToClientResult) => {
        dispatch(upsertClientAsync({ ...client, ...result }));
        setFormMode(FormMode.Send);
    }

    const handleSubmit = (values: Client) => {
        if (formMode === FormMode.None) {
            dispatch(upsertClientAsync(cleanValues(values)));
            setFormMode(FormMode.Submit);
        }
    }

    useEffect(() => {
        if (formMode === FormMode.Submit) {
            if (upsertClientResult?.clientId) {
                (async () => {
                    dispatch(clearUpsertClientResult());
                    await dispatch(getClientAsync(clientId));
                    setFormMode(FormMode.Get);
                })();
            }
        } else if (formMode === FormMode.Get) {
            if (client?.clientId) {
                setFormMode(FormMode.None);
                nextRoute(client);
            }
        } else if (formMode === FormMode.Send) {
            if (upsertClientResult?.clientId) {
                setFormMode(FormMode.None);
                dispatch(clearClient());
                dispatch(setMessage({ text: 'The application has been sent successfully.' }));
                history.replace(`/adviser/${clientId}/client-application`);
            }
        }
    }, [client, upsertClientResult, formMode]);

    return <>
        <FormikWrapper
            validationSchema={yup.object({ products: yup.array().min(1, 'At least one product must be selected') })}
            initialValues={{ ...client }}
            onSubmit={handleSubmit}>
            {({ isValid, values }) => <>
                <Grid container spacing={3} justifyContent="center">
                    <Grid item>
                        <TitleText hideMarginBottom>Client would like to join...</TitleText>
                    </Grid>
                    <Grid item xs={12}>
                        <SelectProduct client={values} hideMultipleHint skipCheckAge={!values.dateOfBirth} />
                    </Grid>
                    <Grid item md={7} sm={8} xs={12}>
                        <Grid container spacing={2} justifyContent="flex-end">
                            {hasSelectedProductRoles(user, values?.products) && 
                                <Grid item>
                                    <Button variant="contained" type="submit" disabled={formMode !== FormMode.None} onClick={() => isValid && openSendToClient(values)}>Send to client</Button>
                                </Grid>
                            } 
                            <Grid item>
                                <Button variant="contained" type="submit" color="primary" disabled={formMode !== FormMode.None}>Next</Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </>}
        </FormikWrapper>
        <SendApplicationToClient hasMobile={!!client?.mobile} open={formMode === FormMode.Confirm} cancel={() => setFormMode(FormMode.None)} ok={sendToClient} />
    </>
}

export default SelectProducts;
